"use client"

import { useEffect, useState } from "react"
import { getDatabase, ref, onValue, set, get } from "firebase/database"
import "./accountDetails.css"
import { FaInfoCircle, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa"

// Update the DepotAccountForm component to accept adminView and selectedBrand props
const DepotAccountForm = ({ adminView = false, selectedBrand = "" }) => {
  const db = getDatabase()

  // States
  const [userToken, setUserToken] = useState("")
  const [companies, setCompanies] = useState([])
  const [selectedCompany, setSelectedCompany] = useState("")
  const [siteList, setSiteList] = useState([])
  const [selectedSite, setSelectedSite] = useState("")
  const [formData, setFormData] = useState({
    AccountNumber: "",
    BeneficiaryName: "",
    IFSC: "",
    Branch: "",
   
  })
  const [validation, setValidation] = useState({
    AccountNumber: { isValid: true, message: "" },
    BeneficiaryName: { isValid: true, message: "" },
    IFSC: { isValid: true, message: "" },
    Branch: { isValid: true, message: "" },
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  // Add state for brand selection in admin view
  const [brands, setBrands] = useState(["Ultratech", "Shree", "JSW", "JKSuper", "ACC", "Ambuja"])
  const [activeBrand, setActiveBrand] = useState("")

  // Get user token from localStorage or use admin token
  useEffect(() => {
    if (adminView) {
      // For admin view, use admin token or a placeholder
      const adminToken = localStorage.getItem("userToken") || "admin-token"
      setUserToken(adminToken)

      // If selectedBrand is provided via props, use it
      if (selectedBrand) {
        setActiveBrand(selectedBrand)
      }
    } else {
      // For supplier view, get token from localStorage
      const token = localStorage.getItem("userToken")
      if (token) {
        setUserToken(token)
        // For supplier view, get brand from localStorage
        const supplierBrand = localStorage.getItem("supplierBrand")
        if (supplierBrand) {
          setActiveBrand(supplierBrand)
        }
      } else {
        setError("User not authenticated. Please log in again.")
        setIsLoading(false)
      }
    }
  }, [adminView, selectedBrand])

  // Fetch companies
  useEffect(() => {
    if (!userToken) return

    setIsLoading(true)
    const usersRef = ref(db, "profileDashboard")

    onValue(
      usersRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const profileData = snapshot.val()
          const companySet = new Set()

          // Extract unique company names from 'profileDashboard' of all users
          Object.entries(profileData).forEach(([userId, userData]) => {
            if (userData.company_name) {
              // Directly check if company_name exists in the profile
              companySet.add(userData.company_name) // Add to set to ensure uniqueness
            }
          })

          setCompanies(Array.from(companySet)) // Convert set to array
        } else {
          console.log("No users found to extract companies.")
          setCompanies([])
        }
        setIsLoading(false)
      },
      (error) => {
        console.error("Error fetching profile data:", error)
        setError("Failed to load companies. Please try again.")
        setIsLoading(false)
      },
    )
  }, [userToken, db])

  // Fetch sites for the selected company and brand
  useEffect(() => {
    if (!userToken || !selectedCompany) return

    setIsLoading(true)
    setSiteList([])

    // Get the brand - either from state (admin view) or localStorage (supplier view)
    const brand = activeBrand || localStorage.getItem("supplierBrand")
    if (!brand) {
      setError("Supplier brand not found. Please select a brand.")
      setIsLoading(false)
      return
    }

    // Directly fetch from new-code node
    const newCodeRef = ref(db, "new-code")

    onValue(
      newCodeRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const newCodeData = snapshot.val()
          const sites = []

          // Loop through all users in new-code
          Object.entries(newCodeData).forEach(([userId, userData]) => {
            // Check if this user has the brand
            if (userData.brands && userData.brands[brand]) {
              // Check if this user has sites for the brand
              if (userData.brands[brand].sites) {
                // Get the sites for this user
                const userSites = userData.brands[brand].sites

                // Process each site
                Object.entries(userSites).forEach(([siteId, siteData]) => {
                  if (siteData && typeof siteData === "object") {
                    // Check if this site belongs to the selected company
                    // We'll need to fetch the company name for this user from profileDashboard
                    get(ref(db, `profileDashboard/${userId}`))
                      .then((profileSnapshot) => {
                        if (profileSnapshot.exists()) {
                          const profileData = profileSnapshot.val()

                          // Check if this user's company matches the selected company
                          if (profileData.company_name === selectedCompany) {
                            const { address, state, pincode } = siteData
                            if (address && state && pincode) {
                              sites.push({
                                userId: userId,
                                id: siteId,
                                displayAddress: `${address}, ${state}, ${pincode}`,
                                rawData: siteData,
                              })

                              // Update the site list after adding a new site
                              setSiteList([...sites])
                            }
                          }
                        }
                      })
                      .catch((error) => {
                        console.error(`Error fetching profile for user ${userId}:`, error)
                      })
                  }
                })
              }
            }
          })

          // If no sites were found after processing all users
          setTimeout(() => {
            if (sites.length === 0) {
              setError(`No sites found for ${selectedCompany}`)
            } else {
              setError("")
            }
            setIsLoading(false)
          }, 2000) // Give time for the async get operations to complete
        } else {
          setError("No data found in new-code")
          setIsLoading(false)
        }
      },
      (error) => {
        console.error("Error fetching new-code data:", error)
        setError("Failed to load sites. Please try again.")
        setIsLoading(false)
      },
    )
  }, [selectedCompany, userToken, db, activeBrand])

  // Fetch account details if they exist for the selected site
  useEffect(() => {
    if (!userToken || !selectedSite) return

    setIsLoading(true)

    // Get the brand from state or localStorage
    const brand = activeBrand || localStorage.getItem("supplierBrand")
    if (!brand) {
      setError("Supplier brand not found. Please select a brand.")
      setIsLoading(false)
      return
    }

    // Parse the selected site value to get userId and siteId
    const [userId, siteId] = selectedSite.split("-")
    const accountRef = ref(db, `new-code/${userId}/brands/${brand}/sites/${siteId}/depoAccount`)

    get(accountRef)
      .then((snapshot) => {
        if (snapshot.exists()) {
          setFormData(snapshot.val())
        } else {
          // Reset form if no existing data
          setFormData({
            AccountNumber: "",
            BeneficiaryName: "",
            IFSC: "",
            Branch: "",
           
          })
        }
        setIsLoading(false)
      })
      .catch((error) => {
        console.error("Error fetching account details:", error)
        setError("Failed to load account details. Please try again.")
        setIsLoading(false)
      })
  }, [selectedSite, userToken, db, activeBrand])

  // Validation functions
  const validateAccountNumber = (value) => {
    if (!value || typeof value !== 'string' || !value.trim()) {
      return { isValid: false, message: "Account number is required" };
    }
  
    const trimmedValue = value.trim();
  
    // Only uppercase letters and digits, 9-18 characters
    if (!/^[A-Z0-9]{9,18}$/.test(trimmedValue)) {
      return { 
        isValid: false, 
        message: "Account number should be 9-18 characters, uppercase letters and digits only" 
      };
    }
  
    return { isValid: true, message: "" };
  };
  

  const validateBeneficiaryName = (value) => {
    if (!value || typeof value !== 'string' || !value.trim()) {
      return { isValid: false, message: "Beneficiary Name is required" };
    }
  
    const trimmedValue = value.trim();
    if (!/^[A-Za-z\s]{2,50}$/.test(trimmedValue)) {
      return { 
        isValid: false, 
        message: "Beneficiary Name should contain only letters and spaces (2-50 characters)" 
      };
    }
  
    return { isValid: true, message: "" };
  };

  const validateIFSC = (value) => {
    if (!value || typeof value !== 'string' || !value.trim()) {
      return { isValid: false, message: "IFSC code is required" };
    }
  
    const trimmedValue = value.trim();
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(trimmedValue)) {
      return { 
        isValid: false, 
        message: "IFSC code should be in format: AAAA0XXXXXX" 
      };
    }
  
    return { isValid: true, message: "" };
  };
  

  const validateBranch = (value) => {
    if (!value || typeof value !== 'string' || !value.trim()) {
      return { isValid: false, message: "Bank name & branch is required" };
    }
  
    const trimmedValue = value.trim();
    const pattern = /^[A-Za-z0-9\s\-,.&()/]{5,100}$/;
  
    if (!pattern.test(trimmedValue)) {
      return {
        isValid: false,
        message: "Enter a valid bank name and branch (letters, numbers, and common punctuation)",
      };
    }
  
    return { isValid: true, message: "" };
  };
  
  
  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    
    // Validate the field as user types
    let validationResult;
    switch (field) {
      case "AccountNumber":
        validationResult = validateAccountNumber(value);
        break;
      case "BeneficiaryName":
        validationResult = validateBeneficiaryName(value);
        break;
        case "IFSC":
        validationResult = validateIFSC(value);
        break;
      case "Branch":
        validationResult = validateBranch(value);
        break;
      default:
        validationResult = { isValid: true, message: "" };
    }
    
    setValidation({
      ...validation,
      [field]: validationResult
    });
  };

  const validateForm = () => {
    const AccountNumberValidation = validateAccountNumber(formData.AccountNumber);
    const BeneficiaryNameValidation = validateBeneficiaryName(formData.BeneficiaryName);
    const IFSCValidation = validateIFSC(formData.IFSC);
    const BranchValidation = validateBranch(formData.Branch);
  
    
    setValidation({
      AccountNumber: AccountNumberValidation,
      BeneficiaryName: BeneficiaryNameValidation,
      IFSC: IFSCValidation,
      Branch: BranchValidation,
      
    });
    
    return AccountNumberValidation.isValid && 
           BeneficiaryNameValidation.isValid && 
           IFSCValidation.isValid && 
           BranchValidation.isValid;
  };

  const handleSave = async () => {
    if (!userToken || !selectedSite) {
      setError("Please select a site address first");
      return;
    }

    // Validate all form fields before saving
    if (!validateForm()) {
      setError("Please correct the errors in the form");
      return;
    }

    setIsLoading(true);
    setError("");

    // Get the brand from state or localStorage
    const brand = activeBrand || localStorage.getItem("supplierBrand");
    if (!brand) {
      setError("Supplier brand not found. Please select a brand.");
      setIsLoading(false);
      return;
    }

    // Parse the selected site value to get userId and siteId
    const [userId, siteId] = selectedSite.split("-");
    const accountRef = ref(db, `new-code/${userId}/brands/${brand}/sites/${siteId}/depoAccount`);

    try {
      await set(accountRef, formData);
      alert("Bank details saved successfully!");

      // Reset form after successful save
      setFormData({
        AccountNumber: "",
        BeneficiaryName: "",
        IFSC: "",
        Branch: "",
        
      });
      setSelectedSite("");
      setSelectedCompany("");
    } catch (error) {
      console.error("Error saving account details:", error);
      setError("Failed to save bank details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle brand change for admin view
  const handleBrandChange = (e) => {
    setActiveBrand(e.target.value);
    setSelectedCompany("");
    setSelectedSite("");
    setSiteList([]);
  };

  return (
    <div className="account-form-container">
      <h2>Bank Details Submission</h2>

      {isLoading && <div className="loading-indicator">Loading...</div>}

      {error && <div className="error-message">{error}</div>}

      {!isLoading && (
        <>
          {/* Brand selector for admin view */}
          {adminView && (
            <div className="form-group">
              <label>Select Brand:</label>
              <select value={activeBrand} onChange={handleBrandChange} disabled={brands.length === 0}>
                <option value="">--Select Brand--</option>
                {brands.map((brand, index) => (
                  <option key={index} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
              {!activeBrand && <div className="helper-text">Please select a brand first</div>}
            </div>
          )}

          {/* Company selector - only show if brand is selected */}
          {activeBrand && (
            <div className="form-group">
              <label>Select Company:</label>
              <select
                value={selectedCompany}
                onChange={(e) => {
                  setSelectedCompany(e.target.value);
                  setSelectedSite(""); // Reset site selection
                  setError(""); // Clear any errors
                }}
                disabled={companies.length === 0}
              >
                <option value="">--Select Company--</option>
                {companies.map((company, index) => (
                  <option key={index} value={company}>
                    {company}
                  </option>
                ))}
              </select>
              {companies.length === 0 && !isLoading && <div className="no-data-message">No companies found</div>}
            </div>
          )}

          {selectedCompany && (
            <div className="form-group">
              <label>Select Site Address:</label>
              <select
                value={selectedSite}
                onChange={(e) => {
                  setSelectedSite(e.target.value);
                  setError(""); // Clear any errors
                }}
                disabled={siteList.length === 0}
              >
                <option value="">--Select Site Address--</option>
                {siteList.map((site) => (
                  <option key={`${site.userId}-${site.id}`} value={`${site.userId}-${site.id}`}>
                    {site.displayAddress}
                  </option>
                ))}
              </select>
              {siteList.length === 0 && !isLoading && selectedCompany && (
                <div className="no-data-message">No sites found for this company</div>
              )}
            </div>
          )}

          {selectedSite && (
            <div className="bank-details-form">
              <div className="form-info-box">
                <FaInfoCircle className="info-icon" />
                <p>Please enter accurate bank details. Any incorrect detail will lead to non-processing of payment. Please fill carefully and double-check before you proceed.</p>
              </div>
  
              <div className={`form-group ${!validation.AccountNumber.isValid ? 'has-error' : ''}`}>
                <label>
                  Account Number: <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.AccountNumber}
                  onChange={(e) => handleChange("AccountNumber", e.target.value)}
                  placeholder="e.g., 123456789012"
                  required
                  maxLength={18}
                />
                <div className="field-info">
                  <span>Format: 9-18 alphanumeric without spaces or special characters</span>
                  {!validation.AccountNumber.isValid && (
                    <div className="validation-error">
                      <FaExclamationTriangle /> {validation.AccountNumber.message}
                    </div>
                  )}
                </div>
              </div>

              <div className={`form-group ${!validation.BeneficiaryName.isValid ? 'has-error' : ''}`}>
                <label>Beneficiary Name: <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.BeneficiaryName}
                  onChange={(e) => handleChange("BeneficiaryName", e.target.value)}
                  placeholder="e.g. Ramesh Kumar"
                  maxLength={50}
                />
                <div className="field-info">
                  <span>Optional: Enter the valid beneficiary name </span>
                  {!validation.BeneficiaryName.isValid && (
                    <div className="validation-error">
                      <FaExclamationTriangle /> {validation.BeneficiaryName.message}
                    </div>
                  )}
                </div>
              </div>

              <div className={`form-group ${!validation.IFSC.isValid ? 'has-error' : ''}`}>
                <label>
                  IFSC Code: <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.IFSC}
                  onChange={(e) => handleChange("IFSC", e.target.value.toUpperCase())}
                  placeholder="e.g., SBIN0123456"
                  required
                  maxLength={11}
                />
                <div className="field-info">
                  <span>Format: AAAA0XXXXXX (first 4 letters, then 0, then 6 alphanumeric characters)</span>
                  {!validation.IFSC.isValid && (
                    <div className="validation-error">
                      <FaExclamationTriangle /> {validation.IFSC.message}
                    </div>
                  )}
                </div>
              </div>

              <div className={`form-group ${!validation.Branch.isValid ? 'has-error' : ''}`}>
                <label>
                  BankName&Branch: <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={formData.Branch}
                  onChange={(e) => handleChange("Branch", e.target.value)}
                  placeholder="e.g., State Bank of India,Connaught Place, New Delhi"
                  required
                  maxLength={150}
                />
                <div className="field-info">
                  <span>Enter the full official name of the bank and Branch name or location</span>
                  {!validation.Branch.isValid && (
                    <div className="validation-error">
                      <FaExclamationTriangle /> {validation.Branch.message}
                    </div>
                  )}
                </div>
              </div>
              <button 
                className="save-btn" 
                onClick={handleSave} 
                disabled={isLoading || !validation.AccountNumber.isValid || !validation.BeneficiaryName.isValid || !validation.IFSC.isValid || !validation.Branch.isValid}
              >
                {isLoading ? "Saving..." : "Save Bank Details"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DepotAccountForm;
